/**
 * Generate a consistent color for a given string using hash
 * Useful for generating consistent but random-looking colors for RFID tags, badges, etc.
 * @param str - The string to generate a color for
 * @returns A Mantine color name
 */
export const getRandomColor = (str: string): string => {
  const colors = [
    "blue",
    "cyan",
    "teal",
    "green",
    "lime",
    "yellow",
    "orange",
    "red",
    "pink",
    "grape",
    "violet",
    "indigo",
  ];
  // Use string to generate consistent but random-looking color
  const hash = str.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

/**
 * Get color for item status
 * @param status - The item status string
 * @returns A Mantine color name
 */
export const getItemStatusColor = (status: string | null | undefined): string => {
  if (typeof status !== "string") return "gray";
  const statusColorMap: Record<string, string> = {
    in_stock_main: "green",
    in_transit_to_sub: "blue",
    inbound_sub: "cyan",
    in_stock_sub: "lime",
    issued_to_field: "indigo",
    on_site: "violet",
    in_stock: "teal",
    in_delivery: "blue",
    in_retrieval: "orange",
    in_maintenance: "yellow",
    missing: "red",
    incomplete: "gray",
    delivered: "green",
    storm_stock: "grape",
  };

  return statusColorMap[status.toLowerCase()] || "gray";
};

/**
 * Read the active client's brand colour, set at runtime on the document root by
 * ClientProvider. Use this when a colour is needed in imperative (non-CSS) code
 * such as canvas or Google Maps overlays, where the `--client-color` variable
 * cannot be referenced directly.
 * @param fallback - Returned when no client colour has been set yet
 * @returns A CSS colour string
 */
export const getClientBrandColor = (fallback = "#3f81ef"): string => {
  if (typeof document === "undefined") return fallback;

  const value = getComputedStyle(document.documentElement)
    .getPropertyValue("--client-color")
    .trim();

  return value || fallback;
};

/**
 * Pick a legible foreground colour for text drawn on top of `backgroundColor`,
 * using the WCAG relative-luminance formula. Needed wherever the background is
 * client-controlled (brand colours range from near-black to pale yellow) and a
 * hardcoded white or black label would be unreadable.
 * @param backgroundColor - A hex colour string; anything else falls back to light
 * @returns A hex colour string for the text
 */
export const getContrastingTextColor = (
  backgroundColor: string,
  dark = "#1a1b1e",
  light = "#ffffff",
): string => {
  const hex = backgroundColor.trim().replace("#", "");

  if (!/^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) return light;

  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((char) => char + char)
          .join("")
      : hex;

  const toLinear = (value: number) =>
    value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;

  const [r, g, b] = [0, 2, 4].map((offset) =>
    toLinear(parseInt(full.substring(offset, offset + 2), 16) / 255),
  );

  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  return luminance > 0.45 ? dark : light;
};

/**
 * Convert a hex color to rgba string
 * @param hex - The hex color string (e.g. "#3f81ef")
 * @param alpha - The alpha value (0-1)
 * @returns An rgba color string
 */
export const hexToRgba = (hex: string, alpha: number): string => {
  let hexValue = hex.replace("#", "");

  // Handle short hex values: "0" -> "000000", "abc" -> "aabbcc"
  if (hexValue.length <= 2) {
    hexValue = hexValue.padStart(1, "0").repeat(6).substring(0, 6);
  } else if (hexValue.length === 3) {
    hexValue = hexValue[0] + hexValue[0] + hexValue[1] + hexValue[1] + hexValue[2] + hexValue[2];
  } else {
    hexValue = hexValue.padEnd(6, "0");
  }

  const r = parseInt(hexValue.substring(0, 2), 16) || 0;
  const g = parseInt(hexValue.substring(2, 4), 16) || 0;
  const b = parseInt(hexValue.substring(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
